import { importLibrary, setOptions } from '@googlemaps/js-api-loader'
import { usePortalDropdownPosition } from '@hooks'
import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { config } from '../../config'
import {
    AutocompleteContainer,
    AutocompleteDropdown,
    AutocompleteItem,
    ClearButton,
    EmptyAutocompleteItem,
    Input,
    MainText,
    SecondaryText,
} from './LocationAutocomplete.styles'

export interface PlaceSuggestion {
	placeId: string
	primaryText: string
	secondaryText: string
	fullText: string
	types: string[]
}

interface LocationAutocompleteProps {
	value: string
	onChange: (value: string) => void
	onPlaceSelect: (place: PlaceSuggestion) => void
	placeholder?: string
	id?: string
	autoFocus?: boolean
}

const LocationAutocomplete: React.FC<LocationAutocompleteProps> = ({
	value,
	onChange,
	onPlaceSelect,
	placeholder = 'Search for a location...',
	id,
	autoFocus = false,
}) => {
	const [isOpen, setIsOpen] = useState(false)
	const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([])
	const [isLoading, setIsLoading] = useState(false)
	const [autocompleteService, setAutocompleteService] = useState<google.maps.places.AutocompleteService | null>(null)
	const [sessionToken, setSessionToken] = useState<google.maps.places.AutocompleteSessionToken | null>(null)
	const containerRef = useRef<HTMLDivElement>(null)
	const inputRef = useRef<HTMLInputElement>(null)
	const dropdownRef = useRef<HTMLDivElement>(null)
	const dropdownCoords = usePortalDropdownPosition(containerRef, isOpen)

	// Initialize Google Maps API
	useEffect(() => {
		const apiKey = config.googleMapsApiKey
		if (!apiKey) {
			console.error('Google Maps API key is not configured')
			return
		}

		// Use the new functional API
		setOptions({ key: apiKey })

		importLibrary('places')
			.then(() => {
				// After load completes, google is available globally
				const service = new google.maps.places.AutocompleteService()
				const token = new google.maps.places.AutocompleteSessionToken()
				setAutocompleteService(service)
				setSessionToken(token)
			})
			.catch((error: Error) => {
				console.error('Error loading Google Maps API:', error)
			})
	}, [])

	// Fetch autocomplete suggestions
	useEffect(() => {
		if (!value.trim() || !autocompleteService || !sessionToken) {
			setSuggestions([])
			setIsOpen(false)
			return
		}

		// Don't fetch if dropdown is already closed
		if (!isOpen) {
			return
		}

		setIsLoading(true)

		const request: google.maps.places.AutocompletionRequest = {
			input: value,
			sessionToken,
		}

		autocompleteService.getPlacePredictions(request, (predictions, status) => {
			setIsLoading(false)

			if (status !== google.maps.places.PlacesServiceStatus.OK || !predictions) {
				setSuggestions([])
				setIsOpen(false)
				return
			}

			const converted: PlaceSuggestion[] = predictions.map((prediction) => ({
				placeId: prediction.place_id,
				primaryText: prediction.structured_formatting.main_text,
				secondaryText: prediction.structured_formatting.secondary_text || '',
				fullText: prediction.description,
				types: prediction.types || [],
			}))

			setSuggestions(converted)
			setIsOpen(true)
		})
	}, [value, autocompleteService, sessionToken, isOpen])

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			const insideContainer = containerRef.current?.contains(event.target as Node)
			const insideDropdown = dropdownRef.current?.contains(event.target as Node)
			if (!insideContainer && !insideDropdown) {
				setIsOpen(false)
			}
		}

		document.addEventListener('mousedown', handleClickOutside)
		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [])

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		onChange(e.target.value)
		setIsOpen(true)
	}

	const handleSuggestionClick = (suggestion: PlaceSuggestion) => {
		// Update state in the correct order
		setIsOpen(false)
		setSuggestions([])
		onChange(suggestion.fullText)
		onPlaceSelect(suggestion)

		// Create a new session token for the next search
		if (google?.maps?.places?.AutocompleteSessionToken) {
			setSessionToken(new google.maps.places.AutocompleteSessionToken())
		}
	}

	const handleBlur = () => {
		setIsOpen(false)
	}

	const handleClear = () => {
		onChange('')
		setSuggestions([])
		setIsOpen(false)
		inputRef.current?.focus()
	}

	const handleFocus = () => {
		if (suggestions.length > 0) {
			setIsOpen(true)
		}
	}

	return (
		<AutocompleteContainer ref={containerRef}>
			<Input
				ref={inputRef}
				id={id}
				type="text"
				value={value}
				onChange={handleInputChange}
				onFocus={handleFocus}
				onBlur={handleBlur}
				placeholder={placeholder}
				autoComplete="off"
				autoFocus={autoFocus}
			/>
			{value && (
				<ClearButton type="button" onClick={handleClear} aria-label="Clear location">
					×
				</ClearButton>
			)}
			{isOpen &&
				suggestions.length > 0 &&
				dropdownCoords &&
				createPortal(
					<AutocompleteDropdown
						ref={dropdownRef}
						$top={dropdownCoords.top}
						$left={dropdownCoords.left}
						$width={dropdownCoords.width}
					>
						{suggestions.map((suggestion) => (
							<AutocompleteItem
								key={suggestion.placeId}
								onMouseDown={(e) => {
									e.preventDefault() // Prevent input blur
									handleSuggestionClick(suggestion)
								}}
							>
								<MainText>{suggestion.primaryText}</MainText>
								{suggestion.secondaryText && (
									<SecondaryText>{suggestion.secondaryText}</SecondaryText>
								)}
							</AutocompleteItem>
						))}
					</AutocompleteDropdown>,
					document.body,
				)}
			{isLoading &&
				suggestions.length === 0 &&
				value.trim() &&
				dropdownCoords &&
				createPortal(
					<AutocompleteDropdown
						ref={dropdownRef}
						$top={dropdownCoords.top}
						$left={dropdownCoords.left}
						$width={dropdownCoords.width}
					>
						<EmptyAutocompleteItem as="div">
							Searching...
						</EmptyAutocompleteItem>
					</AutocompleteDropdown>,
					document.body,
				)}
		</AutocompleteContainer>
	)
}

export default LocationAutocomplete
